import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import GeneralInput from '@/components/Shared/Form/Inputs/GeneralInput'
import Label from '@/components/Shared/Form/Label'
import FormInput from '@/components/Shared/Form/FormInput'
import { SignInFormData } from '@/types/user.type'
import { SignInFormSchema } from '@/lib/validationSchema'
import AuthLayout from '@/components/User/AuthLayout'
import { SIGN_IN_FORM } from '@/constants/amplitudeEvents'
import GoogleLoginButton from '@/components/User/GoogleLoginButton'
import { ResponseCode } from '@/types/response.type'
import { useAuth } from '@/hooks/useAuth'
import { toast } from '@/hooks/useToast'
import {
  RETURN_PATH_PARAM,
  SESSION_EXPIRED_PARAM,
  SESSION_EXPIRED_VALUE,
} from '@/constants/authRedirect'
import {
  isDesktopAuthorizeReturnPath,
  syncPendingDesktopAuthorizePath,
} from '@/lib/desktopOAuthReturnPath'
import { clearSessionStorage } from '@/lib/sessionCleanup'

declare global {
  interface Window {
    google: any
    onGoogleLibraryLoad: () => any
  }
}

const SignInPage = () => {
  const defaultValues: SignInFormData = {
    email: '',
    password: '',
  }
  const { signInSession } = useAuth()
  const router = useRouter()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)
  const sessionExpiredToastShown = useRef(false)

  const methods = useForm<SignInFormData>({
    resolver: yupResolver(SignInFormSchema),
    mode: 'onChange',
    defaultValues,
  })
  const { formState, handleSubmit, resetField } = methods
  const { isSubmitting, isValid } = formState
  const from = router.query[RETURN_PATH_PARAM]
  const desktopAuthorizeFrom =
    typeof from === 'string' && isDesktopAuthorizeReturnPath(from) ? from : null
  const ssoHref = desktopAuthorizeFrom
    ? `/users/sso?${RETURN_PATH_PARAM}=${encodeURIComponent(desktopAuthorizeFrom)}`
    : '/users/sso'
  const isSessionExpired = router.query[SESSION_EXPIRED_PARAM] === SESSION_EXPIRED_VALUE

  // The middleware sends an expired session here without running client code first;
  // clear the browser-side state the same way sign-out and the in-app expiry path do.
  useEffect(() => {
    if (isSessionExpired) clearSessionStorage()
  }, [isSessionExpired])

  const onSubmit = async (data: SignInFormData) => {
    try {
      setIsSigningIn(true)
      await signInSession(data)
      // Keep the loading state until redirect happens
      // The middleware will handle the redirect, so we don't reset the state here
    } catch (e: any) {
      setIsSigningIn(false) // Reset loading state on error
      // Safe access to error response data
      const errorCode = e?.response?.data?.code

      if (errorCode === ResponseCode.INCORRECT_PASSWORD) {
        resetField('password')
      } else if (errorCode === ResponseCode.RECORD_NOT_EXISTS) {
        resetField('email')
      }
    }
  }

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    if (!router.isReady) return
    syncPendingDesktopAuthorizePath(typeof from === 'string' ? from : null)
  }, [router.isReady, from])

  useEffect(() => {
    if (!router.isReady) return

    if (!isSessionExpired) {
      sessionExpiredToastShown.current = false
      return
    }

    if (sessionExpiredToastShown.current) return
    sessionExpiredToastShown.current = true

    toast({
      text: 'Your session has expired. Please sign in again.',
      type: 'error',
    })
  }, [router.isReady, isSessionExpired])

  return (
    <AuthLayout title={'Sign in'}>
      <div className="sm:mx-auto sm:w-full">
        <h2 className="mt-10 text-[28px] font-semibold leading-9 tracking-tight text-label-strong">
          Sign in
        </h2>
      </div>
      <div className="flex justify-center w-full mt-9">
        <GoogleLoginButton
          disabled={!hasMounted || !router.isReady}
          onBeforeLogin={() => {
            if (desktopAuthorizeFrom) {
              syncPendingDesktopAuthorizePath(desktopAuthorizeFrom)
            }
          }}
        />
      </div>
      <hr className={'mt-7 mb-11'} />
      <div className="sm:mx-auto sm:w-full">
        <FormProvider {...methods}>
          <form
            id={SIGN_IN_FORM}
            name="Sign In Form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
            action="#"
            method="POST"
          >
            <div>
              <div className="mt-2">
                <div className="pb-4 space-y-1">
                  <Label
                    name="email"
                    content="Email"
                    className="text-sm text-label-neutral font-normal"
                  />
                  <FormInput
                    name="email"
                    inputFieldComponent={
                      <GeneralInput
                        type="email"
                        extendClass
                        className={'text-label-normal placeholder:text-label-assistive'}
                        placeholder={'Your email address'}
                      />
                    }
                  />
                </div>
                <div className="pb-2.5 space-y-1">
                  <Label
                    name="password"
                    content="Password"
                    className="text-sm text-label-neutral font-normal"
                  />
                  <FormInput
                    name="password"
                    inputFieldComponent={
                      <GeneralInput
                        type="password"
                        extendClass
                        className={'text-label-normal placeholder:text-label-assistive'}
                        placeholder={'Password'}
                      />
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <button
                type="submit"
                disabled={isSubmitting || !isValid || isSigningIn}
                className="flex w-full justify-center rounded-md bg-primary-normal text-white px-3 py-3 text-base font-medium leading-[26px] hover:bg-primary-strong disabled:bg-interaction-disabled disabled:text-label-disabled disabled:border-none"
              >
                {isSigningIn ? 'Signing In...' : 'Sign In'}
              </button>
            </div>
          </form>
          <Link
            className={
              'mt-4 block text-center w-full text-label-neutral text-[15px] leading-6 font-normal hover:text-primary-normal'
            }
            href={'/users/forgot_password'}
          >
            Forgot password?
          </Link>
          {hasMounted && router.isReady ? (
            <Link
              className={
                'mt-4 block text-center w-full text-label-neutral text-[15px] leading-6 font-normal hover:text-primary-normal'
              }
              href={ssoHref}
            >
              Log in with SSO
            </Link>
          ) : (
            <span
              aria-disabled="true"
              className={
                'mt-4 block text-center w-full text-label-neutral text-[15px] leading-6 font-normal'
              }
            >
              Log in with SSO
            </span>
          )}
        </FormProvider>
        <div className="text-label-alternative text-[12px] mt-4 leading-5 text-center">
          By signing in, you are agreeing to our{' '}
          <Link
            href="https://linqalpha.com/terms-of-use"
            target="_blank"
            rel="noreferrer"
            className="text-label-normal"
          >
            Terms of Use
          </Link>
          {', '}
          <Link
            href="https://linqalpha.com/privacy-policy"
            target="_blank"
            rel="noreferrer"
            className="text-label-normal"
          >
            Privacy Policy
          </Link>{' '}
          and{' '}
          <Link
            href="https://linqalpha.com/disclaimer"
            target="_blank"
            rel="noreferrer"
            className="text-label-normal"
          >
            AI Disclaimer
          </Link>
        </div>
      </div>
    </AuthLayout>
  )
}

export default SignInPage
